import { Box, Card, CardContent, Link, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';
import type { ReactNode } from 'react';

interface HomeLinkCardProps {
  title: string;
  href: string;
  icon: ReactNode;
  external?: boolean;
}

export const HomeLinkCard = ({
  title,
  href,
  icon,
  external = false,
}: HomeLinkCardProps) => {
  const classes = useStyles();
  const content = (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.icon}>{icon}</Box>
        <Box className={classes.text}>
          <Typography className={classes.title}>{title}</Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return external ? (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={classes.link}
    >
      {content}
    </Link>
  ) : (
    <Link component={RouterLink} to={href} className={classes.link}>
      {content}
    </Link>
  );
};

const useStyles = makeStyles(theme => ({
  card: {
    borderRadius: 8,
    border: `1px solid ${theme.palette.border}`,
    boxShadow: 'none',
    height: 50,
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    '&:hover': {
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.action.hover,
    },
  },
  cardContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    height: '100%',
    padding: theme.spacing(1.25, 1.5),
    '&:last-child': { paddingBottom: theme.spacing(1.25) },
  },
  link: {
    display: 'block',
    minWidth: 0,
    height: '100%',
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'none' },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      borderRadius: 8,
    },
  },
  icon: {
    width: 26,
    height: 26,
    borderRadius: 6,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
    '& .MuiSvgIcon-root': { fontSize: 18 },
  },
  text: { flex: 1, minWidth: 0 },
  title: {
    color: theme.palette.text.primary,
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  description: {
    color: theme.palette.text.secondary,
    fontSize: '0.76rem',
    lineHeight: 1.35,
    marginTop: theme.spacing(0.25),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));
